import { createReducer } from '@reduxjs/toolkit';
import { ISiteDto, SiteDto } from '../../api/ClientApi';
import { myAssert } from '../../common/console';

import {
    importSiteForSiteOwnerForm,
    addOrRemoveWarehouseOwner,
    importAllSiteOwners,
    saveSiteOwners,
    clearSiteOwners,
} from '../actions/siteOwnerFormActions';
import { SaveEntityStatus } from './common';

export interface SiteOwnerFormReducerState {
    currentWarehouseOwners: string[] | null;
    allWarehouseOwners: string[] | null;

    site: ISiteDto | null;

    saveStatus: SaveEntityStatus;
}

function clearState(state: SiteOwnerFormReducerState): void {
    state.currentWarehouseOwners = null;
    state.allWarehouseOwners = null;

    state.site = null;

    state.saveStatus = SaveEntityStatus.None;
}

function getInitialState(): SiteOwnerFormReducerState {
    const state: SiteOwnerFormReducerState = {
        currentWarehouseOwners: null,
        allWarehouseOwners: null,

        site: null,

        saveStatus: SaveEntityStatus.None,
    };

    clearState(state);

    return state;
}

export const siteOwnerFormReducer = createReducer<SiteOwnerFormReducerState>(
    getInitialState(), // Initial state
    (builder) => {
        builder
            .addCase(importSiteForSiteOwnerForm.fulfilled, (state: SiteOwnerFormReducerState, action: any) => {
                const newSite: SiteDto = action.payload;

                state.site = newSite ?? new SiteDto();
                state.currentWarehouseOwners = newSite.warehouseOwnersAzObjIds ?? [];
            })
            .addCase(clearSiteOwners, (state: SiteOwnerFormReducerState, action: any) => {
                clearState(state);
            })
            .addCase(addOrRemoveWarehouseOwner, (state: SiteOwnerFormReducerState, action: any) => {
                const newWO: string = action.payload;

                if (!state.currentWarehouseOwners) {
                    state.currentWarehouseOwners = [newWO];
                } else {
                    const woIndex = state.currentWarehouseOwners?.findIndex((wo) => wo === newWO);

                    if (woIndex === undefined || woIndex === -1) {
                        state.currentWarehouseOwners.unshift(newWO);
                    } else {
                        state.currentWarehouseOwners.splice(woIndex, 1);
                    }
                }
            })
            .addCase(importAllSiteOwners.fulfilled, (state: SiteOwnerFormReducerState, action: any) => {
                const siteOwners: string[] = action.payload;

                state.allWarehouseOwners = siteOwners;
            })
            .addCase(saveSiteOwners.pending, (state: SiteOwnerFormReducerState, action: any) => {
                myAssert(() => state.site !== null, 'There needs to be order if save is on its way');
                myAssert(
                    () => state.saveStatus === SaveEntityStatus.None || state.saveStatus === SaveEntityStatus.Error,
                    '',
                    false,
                );

                state.saveStatus = SaveEntityStatus.Pending;
            })
            .addCase(saveSiteOwners.fulfilled, (state: SiteOwnerFormReducerState, action: any) => {
                myAssert(() => state.site !== null, 'Error saving site');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Saved;
            })
            .addCase(saveSiteOwners.rejected, (state: SiteOwnerFormReducerState, action: any) => {
                myAssert(() => state.site !== null, 'Error saving site');
                myAssert(() => state.saveStatus === SaveEntityStatus.Pending, '', false);

                state.saveStatus = SaveEntityStatus.Error;
            });
    },
);
